import {
  Box,
  Center,
  HStack,
  Image,
  VisuallyHidden,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "adminComponents/atoms/Button";
import { Card } from "adminComponents/atoms/Card";
import { Text } from "adminComponents/atoms/Text";
import { GoalsProgressBar } from "adminComponents/molecules/GoalsProgressBar";
import { pxToRem } from "adminComponents/utils";
import { useAuth } from "lib/features/auth";
import { ReactComponent as CapsuleSVG } from "sharedComponents/molecules/SeasonMapHeader/resource/capsule_bg_pink.svg";
import { ReactComponent as TicketIconSVG } from "sharedComponents/organisms/Season1MapHeader/resource/ticket_icon.svg";

import { CapsuleMachineModal } from "../CapsuleMachineModal";

interface IProps {
  seasonName?: string;
  daysLeft?: number;
  currentLevel: number;
  currentXP: number;
  nextLevelXP: number;
  ticketCount?: number;
  imageSrc: string;
  imageAlt: string;
  headingLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
  handleOpenMap: () => void;
  hasRandomGrantableItemsRemaining: boolean;
}

export const SeasonChallengeMapCard: React.FC<IProps> = ({
  seasonName,
  currentLevel,
  currentXP,
  nextLevelXP,
  ticketCount,
  imageSrc,
  imageAlt,
  headingLevel,
  handleOpenMap,
  hasRandomGrantableItemsRemaining,
}) => {
  const { t } = useTranslation("admin", {
    useSuspense: false,
  });
  const { t: tMap } = useTranslation("map", {
    useSuspense: false,
  });
  const [showCapsuleMachineModal, setShowCapsuleMachineModal] = useState(false);

  const styles = useMultiStyleConfig("AdminSeasonChallengeMapCard", {});
  const remainingXP = nextLevelXP - currentXP;

  const isSeasonComplete = remainingXP < 0;

  const { isFeatureEnabled } = useAuth();

  return (
    <>
      <Card
        borderColor="primary.tan"
        backgroundColor="primary.white"
        variant="adminCardMediumBorderMediumPadding"
      >
        <GoalsProgressBar
          variant="adminSeasonProgress"
          headingLevel={headingLevel}
          challenge={{
            title: seasonName || t("seasonChallengeCard.title"),
            description: t(
              isSeasonComplete
                ? "seasonChallengeCard.descriptionComplete"
                : "seasonChallengeCard.description",
              {
                currentLevel,
                remainingXP,
              }
            ),
            xp_threshold: nextLevelXP,
            awarded_xp: currentXP,
          }}
          isComplete={isSeasonComplete}
          hasIcon
        />
        <Box __css={styles.container}>
          <Button
            variant="adminButtonFilled"
            size="xs"
            onClick={handleOpenMap}
            __css={styles.openMapButton}
          >
            {t("seasonChallengeCard.openMapButton")}
          </Button>
          <Button
            __css={styles.ticketButton}
            leftIcon={
              <Box
                display="inline-flex"
                justifyContent="center"
                alignItems="center"
                boxSize={pxToRem(22)}
                pr={pxToRem(4)}
              >
                <TicketIconSVG />
              </Box>
            }
            cursor="default"
            variant="adminSeasonMapTicketCounter"
            size="xs"
          >
            {ticketCount || ""}
            <VisuallyHidden>
              {tMap("season1.header.availableTicketsLabel")}
            </VisuallyHidden>
          </Button>

          <Box __css={styles.imageContainer}>
            <Image src={imageSrc} alt={imageAlt} __css={styles.image} />
          </Box>
        </Box>
        {isFeatureEnabled("playtime.enable_capsule_machine") && (
          <Box __css={styles.container} bgColor="utility.error" p={pxToRem(16)}>
            <HStack
              w="full"
              alignItems="center"
              justifyContent="center"
              gap={pxToRem(16)}
              paddingBottom={pxToRem(12)}
            >
              <Box minH={pxToRem(72)} minW={pxToRem(72)}>
                <CapsuleSVG />
              </Box>
              <Box>
                <Text color="primary.white">
                  {t("seasonChallengeCard.capsuleMachineCTA")}
                </Text>
              </Box>
            </HStack>
            <Center>
              <Button
                variant="adminButtonFilled"
                onClick={() => setShowCapsuleMachineModal(true)}
              >
                {t("common.capsuleMachine")}
              </Button>
            </Center>
          </Box>
        )}
      </Card>
      <CapsuleMachineModal
        isOpen={showCapsuleMachineModal}
        ticketCount={ticketCount || 0}
        onClose={() => setShowCapsuleMachineModal(false)}
        randomItemsAvailable={hasRandomGrantableItemsRemaining}
      />
    </>
  );
};
